import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react'
import { type TouchEvent, useCallback, useEffect, useRef, useState } from 'react'

import type { ScreenshotRead } from '@/client'
import { adminDeviceScreenshotsOptions } from '@/client/@tanstack/react-query.gen'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { ADMIN_CAPTURES_POLL_MS } from '@/lib/polling'
import { formatCompactRelativeTime } from '@/lib/relative-time'
import { cn } from '@/lib/utils'

const RECENT_LIMIT = 5
const SWIPE_THRESHOLD_PX = 48
const NO_FAILURES: ReadonlySet<string> = new Set()

interface PreviewState {
  shots: ScreenshotRead[]
  index: number
}

function imageUrl(screenshotId: string): string {
  return `/api/admin/screenshots/${screenshotId}/image`
}

export function MissingScreenshot({ className, caption }: { className?: string; caption?: string }) {
  return (
    <div
      role="img"
      aria-label="Screenshot unavailable"
      className={cn('flex flex-col items-center justify-center gap-2', className)}
    >
      <ImageOff aria-hidden="true" className="size-5" />
      {caption === undefined ? null : <span className="text-sm">{caption}</span>}
    </div>
  )
}

export function RecentScreenshots({ deviceId }: { deviceId: string }) {
  const [open, setOpen] = useState(false)
  const [preview, setPreview] = useState<PreviewState | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [unavailable, setUnavailable] = useState<ReadonlySet<string>>(NO_FAILURES)
  const swipeStartX = useRef<number | null>(null)

  useEffect(() => {
    if (!open) return
    setNow(Date.now())
    const ticker = setInterval(() => setNow(Date.now()), 1_000)
    return () => clearInterval(ticker)
  }, [open])

  const screenshots = useQuery({
    ...adminDeviceScreenshotsOptions({
      path: { device_id: deviceId },
      query: { limit: RECENT_LIMIT },
    }),
    enabled: open,
    refetchInterval: preview === null ? ADMIN_CAPTURES_POLL_MS : false,
  })

  const page = useCallback((step: number): void => {
    setPreview((current) => {
      if (current === null) return current
      const next = current.index + step
      if (next < 0 || next >= current.shots.length) return current
      return { shots: current.shots, index: next }
    })
  }, [])

  const previewing = preview !== null

  useEffect(() => {
    if (!previewing) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      event.preventDefault()
      page(event.key === 'ArrowLeft' ? -1 : 1)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [previewing, page])

  const markUnavailable = useCallback((screenshotId: string): void => {
    setUnavailable((current) =>
      current.has(screenshotId) ? current : new Set(current).add(screenshotId),
    )
  }, [])

  const onSwipeStart = (event: TouchEvent): void => {
    swipeStartX.current = event.touches[0]?.clientX ?? null
  }

  const onSwipeEnd = (event: TouchEvent): void => {
    const start = swipeStartX.current
    swipeStartX.current = null
    const end = event.changedTouches[0]?.clientX
    if (start === null || end === undefined) return
    const travelled = end - start
    if (Math.abs(travelled) < SWIPE_THRESHOLD_PX) return
    page(travelled < 0 ? 1 : -1)
  }

  const stepOut = (): void => {
    if (preview !== null) {
      setPreview(null)
      return
    }
    setOpen(false)
  }

  const items = screenshots.data?.items ?? []

  const imageBox = 'aspect-[16/10] max-h-[40svh] w-full rounded-md border'
  const scrollRegion = 'min-h-0 touch-pan-y space-y-4 overflow-y-auto overscroll-contain'

  let listBody
  if (screenshots.isPending) {
    listBody = (
      <div role="status" aria-label="Loading screenshots" className={scrollRegion}>
        {Array.from({ length: RECENT_LIMIT }, (_, row) => (
          <Skeleton key={row} className={imageBox} />
        ))}
      </div>
    )
  } else if (items.length === 0) {
    listBody = <p className="text-muted-foreground py-6 text-center text-sm">No screenshots yet.</p>
  } else {
    listBody = (
      <ul className={scrollRegion}>
        {items.map((shot, index) => {
          const relative = formatCompactRelativeTime(shot.captured_at, now)
          return (
            <li key={shot.id}>
              <button
                type="button"
                aria-label={`Screenshot from ${relative}`}
                onClick={() => setPreview({ shots: items, index })}
                className="hover:bg-accent flex w-full flex-col rounded-md text-left active:opacity-70 [-webkit-tap-highlight-color:transparent]"
              >
                {unavailable.has(shot.id) ? (
                  <MissingScreenshot className={cn('text-muted-foreground', imageBox)} />
                ) : (
                  <img
                    src={imageUrl(shot.id)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    onError={() => markUnavailable(shot.id)}
                    className={cn('bg-muted object-contain select-none', imageBox)}
                  />
                )}
                <span className="text-muted-foreground mt-1 w-full text-center text-sm">
                  {relative}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setUnavailable(NO_FAILURES)
          setOpen(true)
        }}
      >
        Screenshot
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) stepOut()
        }}
      >
        {preview === null ? (
          <DialogContent className="grid-rows-[auto_minmax(0,1fr)] max-h-[min(85svh,48rem)] sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Recent screenshots</DialogTitle>
              <DialogDescription>
                The five most recent screenshots stored for this device.
              </DialogDescription>
            </DialogHeader>
            {listBody}
          </DialogContent>
        ) : (
          <PreviewContent
            preview={preview}
            now={now}
            unavailable={unavailable}
            onBack={() => setPreview(null)}
            onPage={page}
            onImageError={markUnavailable}
            onSwipeStart={onSwipeStart}
            onSwipeEnd={onSwipeEnd}
          />
        )}
      </Dialog>
    </>
  )
}

interface PreviewContentProps {
  preview: PreviewState
  now: number
  unavailable: ReadonlySet<string>
  onBack: () => void
  onPage: (step: number) => void
  onImageError: (screenshotId: string) => void
  onSwipeStart: (event: TouchEvent) => void
  onSwipeEnd: (event: TouchEvent) => void
}

function PreviewContent({
  preview,
  now,
  unavailable,
  onBack,
  onPage,
  onImageError,
  onSwipeStart,
  onSwipeEnd,
}: PreviewContentProps) {
  const shot = preview.shots[preview.index]
  const relative = formatCompactRelativeTime(shot.captured_at, now)
  const atLatest = preview.index === 0
  const atOldest = preview.index === preview.shots.length - 1

  return (
    <DialogContent
      aria-describedby={undefined}
      className="h-[100dvh] w-screen max-w-none rounded-none border-0 bg-black/95 p-0"
    >
      <DialogTitle className="sr-only">Screenshot preview</DialogTitle>
      <div className="absolute inset-x-0 top-0 z-10 flex items-center gap-3 bg-gradient-to-b from-black/80 to-transparent p-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="text-white hover:bg-white/10 hover:text-white"
        >
          ‹ Back
        </Button>
        <span className="text-sm text-white/80">{relative}</span>
      </div>
      <div
        onTouchStart={onSwipeStart}
        onTouchEnd={onSwipeEnd}
        className="flex h-full w-full items-center justify-center"
      >
        {unavailable.has(shot.id) ? (
          <MissingScreenshot caption="Screenshot unavailable" className="text-white/70" />
        ) : (
          <img
            src={imageUrl(shot.id)}
            alt={`Screenshot from ${relative}`}
            onError={() => onImageError(shot.id)}
            className="h-full w-full object-contain"
          />
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Newer screenshot"
        disabled={atLatest}
        onClick={() => onPage(-1)}
        className="absolute left-2 top-1/2 z-10 -translate-y-1/2 text-white hover:bg-white/10 hover:text-white"
      >
        <ChevronLeft aria-hidden="true" className="size-6" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Older screenshot"
        disabled={atOldest}
        onClick={() => onPage(1)}
        className="absolute right-2 top-1/2 z-10 -translate-y-1/2 text-white hover:bg-white/10 hover:text-white"
      >
        <ChevronRight aria-hidden="true" className="size-6" />
      </Button>
    </DialogContent>
  )
}
